import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface RentalLocation {
  id: string;
  name: string;
  location_type: string;
  address: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const useRentalLocations = () => {
  return useQuery({
    queryKey: ['rental-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_locations')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as RentalLocation[];
    },
  });
};

export const useAdminRentalLocations = () => {
  return useQuery({
    queryKey: ['admin-rental-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_locations')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data as RentalLocation[];
    },
  });
};

export const useCreateRentalLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (location: Omit<RentalLocation, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('rental_locations')
        .insert(location)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-locations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-locations'] });
      toast.success('Location created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create location');
      console.error('Error creating location:', error);
    },
  });
};

export const useUpdateRentalLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<RentalLocation> }) => {
      const { data, error } = await supabase
        .from('rental_locations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-locations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-locations'] });
      toast.success('Location updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update location');
      console.error('Error updating location:', error);
    },
  });
};

export const useDeleteRentalLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rental_locations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-locations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-locations'] });
      toast.success('Location deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete location');
      console.error('Error deleting location:', error);
    },
  });
};